import DataTable from '@/components/commons/data-table';
import { columnsCustomerOrder } from '@/components/sections/builder/elements/body/customer/customer_order/component/columns';
import { DetailDrawer } from '@/components/sections/builder/elements/body/customer/customer_order/component/detail-drawer';
import TableCustomerOrderMobile from '@/components/sections/builder/elements/body/customer/customer_order/component/table-mobile';
import { CustomerOrderElementDataType } from '@/components/sections/builder/elements/body/customer/customer_order/type';
import { Skeleton } from '@/components/ui/skeleton';
import { dummyCustomerOrders } from '@/constant/dummyData/dummy-customer-order';
import { useCustomerListOrder } from '@/hooks/api/storefront/customer';
import { QueryParams } from '@/hooks/use-pagination';
import useModal from '@/hooks/useModal';
import Cookies from 'js-cookie';
import React from 'react';

const getStatusTabOrder = (tab: string) => {
  let status;
  switch (tab) {
    case 'all':
      status = '';
      break;
    case 'unpaid':
      status = 'unpaid';
      break;
    case 'processing':
      status = 'paid,ready_to_process,processing,shipping,shipped,not_yet_shipped';
      break;
    case 'done':
      status = 'done';
      break;
    case 'cancel':
      status = 'cancel,cancelled,expired';
      break;
    default:
      status = '';
  }
  return status;
};

type TableOrderCustomerProps = {
  content: CustomerOrderElementDataType;
  preview: boolean;
  params?: QueryParams;
  setParams?: (pagination: QueryParams) => void;
};

export function TableCustomerOrder({
  content,
  preview,
  params,
  setParams,
}: TableOrderCustomerProps) {
  const token = Cookies.get('accessToken');
  const detailDrawer = useModal();

  const queryParams = {
    page: params?.pageIndex === 0 ? 1 : params?.pageIndex,
    pageSize: params?.pageSize || 10,
    q: params?.q as string,
    status: getStatusTabOrder(params?.tab as string),
    startDate: params?.startDate,
    endDate: params?.endDate,
  };

  const { data, totalCount, isLoading } = useCustomerListOrder(token as string, queryParams);

  const tableData = React.useMemo(() => {
    if (isLoading) {
      return Array(4).fill({});
    }
    return preview ? dummyCustomerOrders : data || [];
  }, [isLoading, preview, data]);

  const columns = React.useMemo(
    () =>
      isLoading
        ? columnsCustomerOrder(detailDrawer, content).map((column) => ({
            ...column,
            cell: () => <Skeleton className='h-4 w-full' />,
          }))
        : columnsCustomerOrder(detailDrawer, content),
    [isLoading]
  );

  return (
    <>
      <div className='hidden md:block'>
        <DataTable
          data={tableData || []}
          isLoading={isLoading}
          columns={columns}
          totalCount={totalCount || 0}
          pagination={{
            pageIndex: (params?.pageIndex as number) - 1,
            pageSize: params?.pageSize as number,
          }}
          onPageChange={(page) => {
            setParams?.({ ...params, pageIndex: page, pageSize: params?.pageSize || 10 });
          }}
          onPageSizeChange={(size) => {
            setParams?.({ ...params, pageSize: size, pageIndex: 1 });
          }}
        />
      </div>
      <div className='block md:hidden'>
        <TableCustomerOrderMobile
          data={tableData || []}
          isLoading={isLoading}
          totalCount={totalCount || 0}
          pagination={{
            pageIndex: (params?.pageIndex as number) - 1,
            pageSize: params?.pageSize as number,
          }}
          onPageChange={(page) => {
            setParams?.({ ...params, pageIndex: page as number, pageSize: params?.pageSize || 10 });
          }}
          onPageSizeChange={(size) => {
            setParams?.({ ...params, pageSize: size, pageIndex: 1 });
          }}
          drawer={detailDrawer}
        />
      </div>

      <DetailDrawer
        openModal={detailDrawer.isShow}
        setOpenModal={detailDrawer.setIsShow}
        data={detailDrawer.data as { order_id: string }}
        token={token}
        content={content}
      />
    </>
  );
}
